import React, { useCallback, useMemo, useRef, useState } from "react";

import ShipeeziGrid, { Column, GeneralActions } from "Components/AppGrid/shipeezi-new-grid";
import _ from "lodash";
import { useTranslation } from "react-i18next";

import { Color } from "../../../../../../components/Form/Button";
import {
  DespatchProduct,
  DespatchShipmentContainer as Container,
} from "../../../../../../models/despatch";
import { SearchAttribute } from "../../../../../../models/search.attribute";
import { Searches } from "../../../../../../models/shipeezi.table";
import { paginate } from "../../../../../../util/paginate";
import { DespatchEmptySection } from "../../components/EmptySection";
import { PlusIcon, StyledButton } from "../../styles";
import useFilterProducts from "../hooks/useFilterProducts";
import ModalAddProduct, { AddNewProductHandles } from "./ModalAddProduct";
import QuantityInput from "./QuantityInput";
import { MainDiv } from "./styles";

interface Props {
  despatchProducts: DespatchProduct[];
  loading: boolean;
  viewOnly?: boolean;
  updateProducts: (products: DespatchProduct[]) => void;
}

const DespatchProducts = (props: Props) => {
  const { despatchProducts, loading, updateProducts, viewOnly = false } = props;

  const { t: translate } = useTranslation();

  const [searchAttributes, setSearchAttributes] = useState<SearchAttribute[]>([]);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);

  const addProductRef = useRef<AddNewProductHandles>(null);

  const filteredProducts = useMemo(() => {
    let data = despatchProducts;

    if (searchAttributes.length > 0) {
      data = useFilterProducts(despatchProducts, searchAttributes);
    }

    return paginate(data, page > 0 ? page - 1 : 0, pageSize);
  }, [despatchProducts, searchAttributes, page, pageSize]);

  const handleSearch = (searches: Searches) => {
    const attributes: SearchAttribute[] = [];

    if (searches) {
      searches.forEach((search) => {
        Object.getOwnPropertyNames(search).forEach((attribute) => {
          attributes.push({
            attribute,
            values: [search[attribute]],
          });
        });
      });
    }

    setSearchAttributes(attributes);
  };

  const handleQuantityUpdate = (
    value: number,
    productId: number,
    attribute: keyof DespatchProduct
  ) => {
    const productIndex = despatchProducts.findIndex((product) => product.productId === productId);

    if (productIndex !== -1) {
      const productsAux = _.cloneDeep(despatchProducts) as any;

      productsAux[productIndex][attribute] = value;
      updateProducts(productsAux);
    }
  };

  const onAddNewProduct = () => {
    addProductRef.current?.open(despatchProducts.map((product) => product?.container?.id || 0));
  };

  const columns: Column[] = [
    {
      name: "purchaseOrderId",
      title: `${translate("order")} #`,
      hideSort: true,
      searchIdentifier: "purchaseOrderId",
    },
    {
      name: "container",
      title: `${translate("sku")} #`,
      hideSort: true,
      formatText: (container: Container) => container?.product?.sku,
      searchIdentifier: "product.sku",
    },
    {
      name: "container",
      title: translate("productName"),
      hideSort: true,
      formatText: (container: Container) => container?.product?.description,
      searchIdentifier: "product.description",
    },
    {
      name: "status",
      title: translate("status"),
      hideSort: true,
      searchIdentifier: "status",
    },
    {
      name: "containerId",
      title: translate("container"),
      hideSort: true,
      searchIdentifier: "containerId",
    },
    {
      name: "productQuantity",
      title: translate("stocksToDespatch"),
      formatComponent: (value: number, row: DespatchProduct) => (
        <QuantityInput
          value={value}
          row={row}
          disabled={viewOnly}
          attribute="productQuantity"
          handleChange={handleQuantityUpdate}
        />
      ),
      hideSort: true,
      hideSearch: true,
    },
    {
      name: "palletsQuantity",
      title: translate("palletsToDespatch"),
      formatComponent: (value: number, row: DespatchProduct) => (
        <QuantityInput
          value={value}
          row={row}
          disabled={viewOnly}
          attribute="palletsQuantity"
          handleChange={handleQuantityUpdate}
        />
      ),
      hideSort: true,
      hideSearch: true,
    },
  ];

  const renderAddProduct = useCallback(() => {
    return (
      <StyledButton customColor={Color.SECONDARY} onClick={onAddNewProduct}>
        <PlusIcon color="#ffffff" size={18} />
        {translate("addProduct")}
      </StyledButton>
    );
  }, []);

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch"],
      right: ["onAddProduct"],
    },
    handleSearch: {
      onSearch: handleSearch,
    },
    onAddProduct: renderAddProduct(),
  };

  const handleAddProducts = (selectedProducts: DespatchProduct[]) => {
    updateProducts([...despatchProducts, ...selectedProducts]);
  };

  const handleRemoveProduct = (containerId: number) => {
    updateProducts(
      despatchProducts.filter((product) => String(product?.productId) !== String(containerId))
    );
  };

  const renderScreen = () => {
    if (!despatchProducts || !despatchProducts.length) {
      return (
        <DespatchEmptySection
          buttonLabel={translate("addProduct")}
          descriptionScreen={translate("products")?.toLowerCase()}
          onAdd={onAddNewProduct}
          maintenanceScreen
        />
      );
    }

    return (
      <MainDiv data-cy="despatch-products-container">
        <ShipeeziGrid
          keyExtractor={(despatchProduct: DespatchProduct) => String(despatchProduct?.productId)}
          data={filteredProducts}
          columns={columns}
          totalItems={despatchProducts.length}
          loadingData={loading}
          emptyData={translate("noInformationToBeShown")}
          currentPage={page}
          onNextPage={setPage}
          onPreviousPage={setPage}
          onGoToPage={setPage}
          pageSize={pageSize}
          onChangePageSize={setPageSize}
          pageSizeList={[20, 40, 60, 100]}
          generalActions={generalActions}
          positionActionsColumn="right"
          rowActions={{
            handleDelete: (rowId) => handleRemoveProduct(Number(rowId)),
          }}
        />
      </MainDiv>
    );
  };

  return (
    <React.Fragment>
      <ModalAddProduct ref={addProductRef} onAddProducts={handleAddProducts} />
      {renderScreen()}
    </React.Fragment>
  );
};

export default DespatchProducts;
